import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getArticle, ARTICLES } from "@/data/blog";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";

export default function BlogPost() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useSeo({
    title: article ? `${article.title} — Blog Ozentya` : "Article — Ozentya",
    description: article ? article.excerpt : "",
    path: `/blog/${slug}`,
    image: article ? article.cover : undefined,
  });

  useJsonLd(
    "ld-article",
    article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          image: article.cover.startsWith("/") ? `https://ozentya.org${article.cover}` : article.cover,
          datePublished: article.date,
          author: { "@type": "Organization", name: "Ozentya", url: `${SITE_URL}/a-propos` },
          publisher: {
            "@type": "Organization",
            name: "Ozentya",
            url: SITE_URL,
            logo: { "@type": "ImageObject", url: `${SITE_URL}/img/packs/hero.jpg` },
          },
          mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
        }
      : null
  );

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl mb-4">Article introuvable</h1>
        <Link to="/blog" className="text-[var(--oz-gold)] font-semibold">Retour au blog</Link>
      </div>
    );
  }

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div className="px-4 sm:px-8 lg:px-16 max-w-3xl mx-auto py-12 md:py-16">
      <Link to="/blog" className="inline-flex items-center gap-2 py-2 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)] mb-6">
        <ArrowLeft className="w-4 h-4" /> Tous les articles
      </Link>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--oz-muted)]">
        <span>
          {new Date(article.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
        </span>
        <span aria-hidden>•</span>
        <span>
          Par{" "}
          <Link to="/a-propos" className="font-semibold text-[var(--oz-text)] hover:text-[var(--oz-gold)] transition-colors">
            l'équipe Ozentya
          </Link>
        </span>
      </div>
      <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight text-[var(--oz-text)] leading-tight">
        {article.title}
      </h1>
      <div className="mt-8 rounded-[1.5rem] overflow-hidden">
        <img src={article.cover} alt={article.title} className="w-full h-[300px] sm:h-[420px] object-cover" />
      </div>

      <article className="mt-10 space-y-8">
        {article.content.map((s, i) => (
          <div key={i}>
            <h2 className="font-serif text-2xl text-[var(--oz-text)] mb-2">{s.h}</h2>
            <p className="text-base text-[var(--oz-muted)] leading-relaxed">{s.p}</p>
          </div>
        ))}
      </article>

      <div className="mt-14 pt-10 border-t border-[var(--oz-border)]">
        <h3 className="font-serif text-2xl text-[var(--oz-text)] mb-6">À lire aussi</h3>
        <div className="grid sm:grid-cols-2 gap-6">
          {others.map((a) => (
            <Link key={a.slug} to={`/blog/${a.slug}`} className="group flex gap-4 items-center">
              <img src={a.cover} alt={a.title} className="w-24 h-24 rounded-xl object-cover flex-shrink-0" />
              <span className="font-serif text-lg text-[var(--oz-text)] group-hover:text-[var(--oz-gold)] transition-colors leading-snug">{a.title}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
